import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
/**
 * usePlasmaThread Hook
 *
 * Manages AI thread state following Convex patterns.
 * Thread selection syncs with URL search params for deep linking.
 */
import { useAction, useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useState } from "react";

import {
  useActiveOrganization,
  useActiveOrganizationConvexId,
  useCurrentUser,
} from "~/integrations/vortex-auth/hooks";
import { logger, PortalEvents } from "~/lib/logger";

// Convex function references (avoids TS2589 from deep api.ai.* type chains)
const aiQueriesGetThreadDetails = api.ai.queries.getThreadDetails;

const aiThreadsCreate = api.ai.threads.create;

const aiThreadsSetFocus = api.ai.threads.setFocus;

// Helper to get aiThread from URL search params
function getAiThreadFromSearch(): string | null {
  if (globalThis.window === undefined) {
    return null;
  }
  const parameters = new URLSearchParams(globalThis.location.search);
  return parameters.get("aiThread");
}

interface UsePlasmaThreadOptions {
  /** Initial location focus (optional) */
  locationId?: Id<"locations">;
}

interface UsePlasmaThreadReturn {
  /** Current thread ID (null = no thread selected, start new) */
  threadId: string | null;
  /** Select an existing thread */
  selectThread: (threadId: string) => void;
  /** Start a new conversation (clears current thread) */
  startNewThread: () => void;
  /** Create empty thread (call sendMessage separately for optimistic updates) */
  createThread: () => Promise<string>;
  /** Current location focus */
  locationFocus: Id<"locations"> | undefined;
  /** Update location focus for the thread */
  setLocationFocus: (locationId: Id<"locations"> | undefined) => Promise<void>;
  /** Convex organization ID (for queries) */
  organizationId: Id<"organizations"> | null;
  /** User ID for queries */
  userId: string | null | undefined;
  /** Whether we're ready to make API calls */
  isReady: boolean;
  /** Organization info from Convex */
  organizationInfo: { id: Id<"organizations">; name: string } | null;
  /** Thread details including focus state */
  threadDetails: {
    threadId: string;
    organizationId: Id<"organizations">;
    organizationName: string;
    locationId: Id<"locations"> | null;
    locationName: string | null;
    isOrgWide: boolean;
    createdAt: number;
  } | null;
}

export function usePlasmaThread(
  options: UsePlasmaThreadOptions = {}
): UsePlasmaThreadReturn {
  const { user } = useCurrentUser();
  const userId = user?.id;
  const { organization } = useActiveOrganization();
  const { organizationId } = useActiveOrganizationConvexId();

  // Thread ID from URL search params
  const [threadId, setThreadIdState] = useState<string | null>(() =>
    getAiThreadFromSearch()
  );
  const [locationFocus, setLocationFocusState] = useState<
    Id<"locations"> | undefined
  >(options.locationId);

  // Convex organization id + display name (replaces the legacy
  // getOrganizationByOrgId round-trip).
  const organizationInfo =
    organizationId && organization
      ? { id: organizationId, name: organization.name }
      : null;

  const threadDetails = useQuery(
    aiQueriesGetThreadDetails,
    threadId ? { threadId } : "skip"
  );

  const createThreadAction = useAction(aiThreadsCreate);
  const setFocusMutation = useMutation(aiThreadsSetFocus);

  // Sync thread ID with URL when popstate occurs (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlThreadId = getAiThreadFromSearch();
      if (urlThreadId !== threadId) {
        setThreadIdState(urlThreadId);
      }
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [threadId]);

  // Sync location focus from thread details
  useEffect(() => {
    if (threadDetails?.locationId !== undefined) {
      setLocationFocusState(threadDetails.locationId ?? undefined);
    }
  }, [threadDetails?.locationId]);

  // Update URL when thread changes
  const selectThread = useCallback((newThreadId: string) => {
    setThreadIdState(newThreadId);
    // Update URL search params
    const url = new URL(globalThis.location.href);
    url.searchParams.set("aiThread", newThreadId);
    globalThis.history.replaceState({}, "", url.toString());
    logger.info(PortalEvents.AI_THREAD_SELECTED, {
      thread_id: newThreadId,
    });
  }, []);

  // Start new thread (clear selection)
  const startNewThread = useCallback(() => {
    setThreadIdState(null);
    // Remove aiThread from URL
    const url = new URL(globalThis.location.href);
    url.searchParams.delete("aiThread");
    globalThis.history.replaceState({}, "", url.toString());
    logger.info(PortalEvents.AI_THREAD_STARTED_NEW);
  }, []);

  // Create empty thread (message is sent separately for optimistic updates)
  const createThread = useCallback(async (): Promise<string> => {
    if (!(userId && organizationInfo?.id)) {
      throw new Error("Not authenticated or organization not found");
    }

    const result = await createThreadAction({
      organizationId: organizationInfo.id,
      userId,
      locationId: locationFocus,
    });

    return result.threadId;
  }, [userId, organizationInfo?.id, locationFocus, createThreadAction]);

  // Update location focus
  const setLocationFocus = useCallback(
    async (locationId: Id<"locations"> | undefined): Promise<void> => {
      setLocationFocusState(locationId);
      logger.info(PortalEvents.AI_FOCUS_CHANGED, {
        thread_id: threadId,
        location_id: locationId,
      });

      // If we have an active thread, update its focus
      if (threadId) {
        await setFocusMutation({
          threadId,
          locationId,
        });
      }
    },
    [threadId, setFocusMutation]
  );

  // Check if we're ready to make API calls
  const isReady = Boolean(userId && organizationInfo?.id);

  return {
    threadId,
    selectThread,
    startNewThread,
    createThread,
    locationFocus,
    setLocationFocus,
    organizationId: organizationInfo?.id ?? null,
    userId,
    isReady,
    organizationInfo: organizationInfo ?? null,
    threadDetails: threadDetails
      ? {
          ...threadDetails,
          locationName: threadDetails.locationName,
        }
      : null,
  };
}
